import type {
  ContainerNode,
  HeadingNode,
  ImageNode,
  InfoHtmlDocument,
  InfoHtmlNode,
  InfoHtmlStyle,
  ListItemNode,
  ListNode,
  ParagraphNode,
  TableCellNode,
  TableNode,
  TableRowNode,
  WinlineGridNode,
} from "./InfoHtmlNode";

/**
 * Font-size + layout defaults the renderer applies to every node
 * defaults, kept the single source of truth for body-text sizing across the info page.
 */
export interface InfoDomRendererDefaults {
  /** `<h1>` and `<h2>` font sizes (design px). Inline `style="font-size: Npx"` still wins per-element. */
  headingFontSizeByLevel: { 1: number; 2: number };
  /** Body paragraph font size (design px). */
  paragraphFontSize: number;
  /** Vertical spacing between sibling block-level nodes when no `margin` style is set. */
  defaultSiblingSpacing: number;
}

export const INFO_DOM_RENDERER_DEFAULTS: InfoDomRendererDefaults = {
  headingFontSizeByLevel: { 1: 56, 2: 44 },
  paragraphFontSize: 34,
  defaultSiblingSpacing: 8,
};

export interface InfoDomRenderOptions {
  /** `<img src>` → data URL (null = leave the slot empty, as the image view did). */
  resolveImage: (src: string) => Promise<string | null>;
  defaults?: Partial<InfoDomRendererDefaults>;
}

/**
 * The `Layout` spacing each authored html prefab carried — added ON TOP of the renderer's
 * spacer nodes, exactly as the prefabs stacked them (content root 10, list 8, list item 8, table 4, cell 8).
 */
const LAYOUT_SPACING = { content: 10, container: 0, list: 8, listItem: 8, table: 4, cell: 8, winlineGrid: 16 } as const;
/** The `htmlListItem` prefab indents its content root 32 from the left. */
const LIST_ITEM_INDENT = 32;
/** The `htmlTableCell` prefab pads 4 above and below. */
const CELL_PADDING_Y = 4;
/** `Label.lineHeight` of the paragraph prefab (absolute design px). */
const PARAGRAPH_LINE_HEIGHT = 40;

/** `n` design units as CSS, through the `--du` custom property the page sets (CSS px per design unit). */
export function du(n: number): string {
  return `calc(var(--du) * ${n})`;
}

/**
 * Walks an `InfoHtmlDocument` and builds DOM for it: block nodes stack vertically, table rows run
 * horizontally with vertically centred cells, inline styles map to CSS one to one, sizes are design
 * units (`--du`). Replaces `target`'s children.
 */
export function renderInfoHtmlToDom(doc: InfoHtmlDocument, target: HTMLElement, options: InfoDomRenderOptions): void {
  const renderer = new DomRenderer({ ...INFO_DOM_RENDERER_DEFAULTS, ...options.defaults }, options.resolveImage);
  target.replaceChildren();
  styleColumn(target, LAYOUT_SPACING.content);
  renderer.appendChildren(target, doc.children);
}

class DomRenderer {
  public constructor(
    private readonly defaults: InfoDomRendererDefaults,
    private readonly resolveImage: (src: string) => Promise<string | null>,
  ) {}

  /** `appendChildren`: default sibling spacing unless a margin is authored (the first child gets none). */
  public appendChildren(parent: HTMLElement, children: InfoHtmlNode[]): void {
    children.forEach((node, index) => {
      const rendered = this.renderNode(node);
      if (!rendered) return;
      const style = node.style;
      const top = index === 0 ? (style?.marginTop ?? 0) : (style?.marginTop ?? this.defaults.defaultSiblingSpacing);
      if (top > 0) rendered.style.marginTop = du(top);
      if ((style?.marginBottom ?? 0) > 0) rendered.style.marginBottom = du(style!.marginBottom!);
      parent.appendChild(rendered);
    });
  }

  private renderNode(node: InfoHtmlNode): HTMLElement | null {
    switch (node.kind) {
      case "heading":
        return this.renderHeading(node);
      case "paragraph":
        return this.renderParagraph(node);
      case "image":
        return this.renderImage(node);
      case "list":
        return this.renderList(node);
      case "list_item":
        return this.renderListItem(node);
      case "table":
        return this.renderTable(node);
      case "table_row":
        return this.renderTableRow(node);
      case "table_cell":
        return this.renderTableCell(node);
      case "container":
        return this.renderContainer(node);
      case "winline_grid":
        return this.renderWinlineGrid(node);
    }
  }

  private renderHeading(node: HeadingNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-heading";
    el.textContent = node.text;
    el.style.fontSize = du(node.style?.fontSize ?? this.defaults.headingFontSizeByLevel[node.level]);
    applyTextStyle(el, node.style);
    applyBoxStyle(el, node.style, false);
    return el;
  }

  private renderParagraph(node: ParagraphNode): HTMLElement {
    const el = document.createElement("p");
    el.className = "slot-info-paragraph";
    el.textContent = node.text;
    el.style.fontSize = du(node.style?.fontSize ?? this.defaults.paragraphFontSize);
    el.style.lineHeight = du(PARAGRAPH_LINE_HEIGHT);
    applyTextStyle(el, node.style);
    applyBoxStyle(el, node.style, false);
    return el;
  }

  private renderImage(node: ImageNode): HTMLElement {
    const el = document.createElement("span");
    el.className = "slot-info-image";
    // The `htmlImage` prefab box (64 × 0): only the authored `width` / `height` make it visible.
    el.style.width = du(node.style?.width ?? 64);
    el.style.height = du(node.style?.height ?? 0);
    if (node.alt) el.setAttribute("aria-label", node.alt);
    applyBoxStyle(el, node.style, false);
    void this.resolveImage(node.src).then((url) => {
      if (url && el.isConnected !== false) el.style.backgroundImage = `url("${url}")`;
    });
    return el;
  }

  private renderList(node: ListNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-list";
    styleColumn(el, LAYOUT_SPACING.list);
    for (const item of node.items) {
      const child = this.renderNode(item);
      if (child) appendWithMargins(el, child, item.style);
    }
    applyBoxStyle(el, node.style, true);
    return el;
  }

  private renderListItem(node: ListItemNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-list-item";
    styleColumn(el, LAYOUT_SPACING.listItem);
    el.style.paddingLeft = du(LIST_ITEM_INDENT);
    this.appendChildren(el, node.children);
    applyBoxStyle(el, node.style, true);
    return el;
  }

  private renderTable(node: TableNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-table";
    styleColumn(el, LAYOUT_SPACING.table);
    for (const row of node.rows) {
      const child = this.renderNode(row);
      if (child) appendWithMargins(el, child, row.style);
    }
    applyBoxStyle(el, node.style, true);
    return el;
  }

  private renderTableRow(node: TableRowNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-table-row";
    for (const cell of node.cells) {
      const child = this.renderNode(cell);
      if (!child) continue;
      if ((cell.style?.marginLeft ?? 0) > 0) child.style.marginLeft = du(cell.style!.marginLeft!);
      if ((cell.style?.marginRight ?? 0) > 0) child.style.marginRight = du(cell.style!.marginRight!);
      el.appendChild(child);
    }
    applyBoxStyle(el, node.style, true);
    return el;
  }

  private renderTableCell(node: TableCellNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-table-cell";
    styleColumn(el, LAYOUT_SPACING.cell);
    el.style.paddingTop = du(CELL_PADDING_Y);
    el.style.paddingBottom = du(CELL_PADDING_Y);
    this.appendChildren(el, node.children);
    // `<th>`: every label inside goes bold unless the cell says `font-weight: normal`.
    if (node.isHeader && node.style?.fontWeight !== "normal") el.style.fontWeight = "700";
    applyBoxStyle(el, node.style, true);
    return el;
  }

  private renderContainer(node: ContainerNode): HTMLElement {
    const el = document.createElement(node.sourceTag === "section" ? "section" : "div");
    el.className = "slot-info-container";
    styleColumn(el, LAYOUT_SPACING.container);
    this.appendChildren(el, node.children);
    applyBoxStyle(el, node.style, true);
    return el;
  }

  private renderWinlineGrid(node: WinlineGridNode): HTMLElement {
    const el = document.createElement("div");
    el.className = "slot-info-winlines";
    styleColumn(el, LAYOUT_SPACING.winlineGrid);
    for (const line of node.lines) {
      const grid = document.createElement("div");
      grid.className = "slot-info-winline";
      grid.style.gridTemplateColumns = `repeat(${node.cols}, ${du(24)})`;
      for (let i = 0; i < node.cols * node.rows; i++) {
        const cell = document.createElement("span");
        cell.className = line.activeCells[i] ? "slot-info-winline-cell is-active" : "slot-info-winline-cell";
        grid.appendChild(cell);
      }
      el.appendChild(grid);
    }
    applyBoxStyle(el, node.style, true);
    return el;
  }
}

/** A vertical stack with `Layout` spacing of the prefab. */
function styleColumn(el: HTMLElement, spacing: number): void {
  el.style.display = "flex";
  el.style.flexDirection = "column";
  el.style.alignItems = "stretch";
  if (spacing > 0) el.style.rowGap = du(spacing);
}

/** `appendWithMargins`: only authored margins, no default spacing (the container's layout gap applies). */
function appendWithMargins(parent: HTMLElement, child: HTMLElement, style: InfoHtmlStyle | undefined): void {
  if ((style?.marginTop ?? 0) > 0) child.style.marginTop = du(style!.marginTop!);
  if ((style?.marginBottom ?? 0) > 0) child.style.marginBottom = du(style!.marginBottom!);
  parent.appendChild(child);
}

function applyTextStyle(el: HTMLElement, style: InfoHtmlStyle | undefined): void {
  if (!style) return;
  if (style.fontWeight === "bold") el.style.fontWeight = "700";
  if (style.fontWeight === "normal") el.style.fontWeight = "500";
  if (style.color) el.style.color = style.color;
  if (style.textAlign) el.style.textAlign = style.textAlign;
}

/** Padding only lands on nodes that carried a `Layout`; size and background on any node. */
function applyBoxStyle(el: HTMLElement, style: InfoHtmlStyle | undefined, hasLayout: boolean): void {
  if (!style) return;
  if (hasLayout) {
    if (style.paddingTop !== undefined) el.style.paddingTop = du(style.paddingTop);
    if (style.paddingRight !== undefined) el.style.paddingRight = du(style.paddingRight);
    if (style.paddingBottom !== undefined) el.style.paddingBottom = du(style.paddingBottom);
    if (style.paddingLeft !== undefined) el.style.paddingLeft = du(style.paddingLeft);
  }
  if (style.width !== undefined) {
    el.style.width = du(style.width);
    el.style.flexShrink = "0";
  }
  if (style.height !== undefined) el.style.height = du(style.height);
  if (style.backgroundColor) el.style.backgroundColor = style.backgroundColor;
}
